import React, {useEffect, useState} from 'react';
import {useParams, useSearchParams, useNavigate} from 'react-router-dom';
import {
    Box,
    Text,
    Image,
    VStack,
    HStack,
    Center,
    Spinner,
    ButtonGroup,
    ButtonProps,
    Card,
    CardHeader,
    CardBody,
    Flex,
    GridItem,
    Grid,
    Button,
    type GridItemProps,
    FormControl,
    FormLabel,
    Input,
    Select,
    useToast,
    Checkbox,
    CheckboxGroup,
} from '@chakra-ui/react';
import {FaHeart, FaComment, FaEye, FaShare} from 'react-icons/fa';
import {customAuthAxios} from '../../../utils/customAuthAxios';
import {getDjangoToken} from '../../../utils/instagram/services/apiService';

export const TableHeaderCell = (props: GridItemProps) => {
    return (
        <GridItem
            bg="gray.200"
            textAlign="left"
            py={2}
            fontWeight="bold"
            paddingLeft="10px"
            {...props}
        />
    );
};

interface InstagramDmContent {
    id: number;
    name: string;
    type: string;
    valid_period?: {
        start_at: string;
        end_at: string;
    };
}

interface InstagramPackage {
    id: number;
    package_name: string;
    dm_contents: number[];
}

const InstagramPackageCreate: React.FC = () => {
    const {restaurantId} = useParams<{restaurantId: string}>();
    const [searchParams] = useSearchParams();
    const editId = searchParams.get('edit');
    const navigate = useNavigate();
    const toast = useToast();
    const [postDetails, setPostDetails] = useState<any>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true); // ローディング状態
    const [packageName, setPackageName] = useState('');
    const [dmContents, setDmContents] = useState<InstagramDmContent[]>([]);
    const [selectedContent, setSelectedContent] = useState<string>('');
    const [formData, setFormData] = useState<InstagramPackage>({
        id: 0,
        package_name: '',
        dm_contents: [],
    });

    useEffect(() => {
        const fetchPostDetails = async () => {
            try {
                setIsLoading(true); // ローディング開始
                const djangoToken = await getDjangoToken();
                const response = await customAuthAxios.get(
                    `/external_api/instagram-auth/metrics/${restaurantId}/`,
                    {
                        headers: {Authorization: `Bearer ${djangoToken}`},
                    }
                );
                setPostDetails(response.data);
            } catch (error) {
                console.error('Failed to fetch post details:', error);
            } finally {
                setIsLoading(false); // ローディング終了
            }
        };

        const fetchDmContents = async () => {
            try {
                const djangoToken = await getDjangoToken();
                const response = await customAuthAxios.get(
                    `/api/restaurants/${restaurantId}/instagram/dm/list_for_bot/`,
                    {
                        headers: {Authorization: `Bearer ${djangoToken}`},
                    }
                );
                console.log('DM Contents Response:', response.data);
                setDmContents(response.data);
            } catch (error) {
                console.error('Failed to fetch DM contents:', error);
                toast({
                    title: 'エラー',
                    description: 'DMコンテンツの取得に失敗しました',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
            }
        };

        const fetchPackage = async () => {
            if (!editId) return;

            try {
                const djangoToken = await getDjangoToken();
                const response = await customAuthAxios.get(
                    `/api/restaurants/${restaurantId}/instagram/packages/${editId}/`,
                    {
                        headers: {Authorization: `Bearer ${djangoToken}`},
                    }
                );
                setFormData(response.data);
            } catch (error) {
                console.error('Failed to fetch package:', error);
                toast({
                    title: 'エラー',
                    description: 'パッケージの取得に失敗しました',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
            }
        };

        if (restaurantId) {
            fetchDmContents();
        }

        fetchPostDetails();
        fetchPackage();
    }, [restaurantId, editId]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const djangoToken = await getDjangoToken();
            if (editId) {
                await customAuthAxios.put(
                    `/api/restaurants/${restaurantId}/instagram/packages/${editId}/`,
                    formData,
                    {
                        headers: {Authorization: `Bearer ${djangoToken}`},
                    }
                );
            } else {
                await customAuthAxios.post(
                    `/api/restaurants/${restaurantId}/instagram/packages/`,
                    formData,
                    {
                        headers: {Authorization: `Bearer ${djangoToken}`},
                    }
                );
            }

            toast({
                title: '成功',
                description: editId ? 'パッケージを更新しました' : 'パッケージを作成しました',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            navigate(`/restaurants/${restaurantId}/instagram/bot/settings`);
        } catch (error) {
            console.error('Failed to save package:', error);
            toast({
                title: 'エラー',
                description: editId ? 'パッケージの更新に失敗しました' : 'パッケージの作成に失敗しました',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDmContentsChange = (value: string[]) => {
        setFormData(prev => ({
            ...prev,
            dm_contents: value.map(v => parseInt(v)),
        }));
    };

    // if (isLoading) {
    //     return (
    //         <Center height="200px">
    //             <Spinner size="xl" color="blue.500" />
    //         </Center>
    //     );
    // }
    //
    // if (!postDetails) return <Text>データが見つかりません。</Text>;

    return (
        <Box p={4} maxW="800px" mx="auto">
            <Card>
                <CardHeader>
                    <Text fontWeight="bold" fontSize="lg">
                        {editId ? 'パッケージの編集' : 'パッケージの新規作成'}
                    </Text>
                </CardHeader>
                <CardBody>
                    <form onSubmit={handleSubmit}>
                        <VStack spacing={6} align="stretch">
                            <FormControl isRequired>
                                <FormLabel>パッケージ名</FormLabel>
                                <Input
                                    value={formData.package_name}
                                    onChange={(e) => setFormData(prev => ({...prev, package_name: e.target.value}))}
                                    placeholder="パッケージ名を入力"
                                />
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel>DMコンテンツ</FormLabel>
                                <CheckboxGroup
                                    value={formData.dm_contents.map(String)}
                                    onChange={handleDmContentsChange}
                                >
                                    <VStack align="start" spacing={2}>
                                        {dmContents.map((content) => (
                                            <Checkbox key={content.id} value={String(content.id)}>
                                                {content.name}
                                            </Checkbox>
                                        ))}
                                    </VStack>
                                </CheckboxGroup>
                            </FormControl>

                            <Button
                                type="submit"
                                colorScheme="blue"
                                isLoading={isLoading}
                                loadingText={editId ? '更新中...' : '作成中...'}
                            >
                                {editId ? '更新' : '作成'}
                            </Button>
                        </VStack>
                    </form>
                </CardBody>
            </Card>
        </Box>
    );
};

export default InstagramPackageCreate;
